/**
 * Лента уведомлений — `GET /api/v1/notifications/me` (Блок 2 · Ф7 · В11-a).
 *
 * Отдельный файл, а не дописка в `api/notifications.ts`: там — НАСТРОЙКИ
 * напоминаний (`/users/me/notifications`), другой маршрут и другой ключ кэша.
 *
 * Разбор ответа — `.parse()` общей zod-схемой на каждой странице: расхождение
 * формы становится ошибкой запроса, а не пустым местом в панели.
 *
 * ═══ БЮДЖЕТ ЗАПРОСОВ — ПОЧЕМУ ТАКТ 180 С, А НЕ 60 ══════════════════════════
 *
 * Колокольчик стоит в шапке КАЖДОГО экрана, а запросы всего офиса идут через
 * один адрес (NAT) в один бакет nginx: 120 запросов в минуту на IP, всплеск 40
 * (`infra/nginx/nginx.conf`, `infra/nginx/conf.d/aris.conf`). Фон опроса обязан
 * оставлять бюджет настоящим запросам:
 *
 *   | людей × видимых вкладок | такт 60 с | такт 180 с | доля от 120 |
 *   |-------------------------|-----------|------------|-------------|
 *   | 14 × 1                  | 14 /мин   | 4,7 /мин   | 4 %         |
 *   | 14 × 2                  | 28 /мин   | 9,3 /мин   | 8 %         |
 *   | 40 × 1                  | 40 /мин   | 13,3 /мин  | 11 %        |
 *   | 40 × 2                  | 80 /мин   | 26,7 /мин  | 22 %        |
 *
 * Отсюда четыре правила, и каждое держит свою часть бюджета:
 *
 *   1. такт — `NOTIFICATION_FEED_POLL_MS`, только у ВИДИМОЙ вкладки;
 *   2. тактом перезапрашивается ТОЛЬКО первая страница — она кормит счётчик.
 *      Страницы, догруженные «Показать ещё», живут в состоянии панели и
 *      тактом не трогаются вовсе (у `useInfiniteQuery` такт перезапрашивал бы
 *      их все, по запросу на страницу);
 *   3. возврат фокуса в окно перезапрашивает ленту не чаще раза в
 *      `NOTIFICATION_FEED_FOCUS_MIN_MS` — пока ответ свежее, фокус ничего не
 *      шлёт;
 *   4. отказ опроса не повторяется сам (следующий такт и есть повтор) и не
 *      показывает тост: запрос идёт с флагом `background`;
 *   5. возврат сети ленту не перезапрашивает (`refetchOnReconnect: false`).
 *
 * ⚠️ ТАБЛИЦА ВЫШЕ — ТОЛЬКО ТАКТ. С фокусом и открытием панели потолок на
 * вкладку в фокусе — 1,33 запроса в минуту: реалистичный худший случай для 40
 * человек — около 30 % бюджета, теоретический потолок — 55 % (пересчёт
 * приёмки da). Арифметика, не замер.
 */
import { useQuery } from '@tanstack/react-query';

import { NotificationFeedResponseSchema, type NotificationFeedResponse } from '@aris/shared';

import { apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

const FEED_PATH = '/notifications/me';

/** Такт фонового опроса первой страницы. */
export const NOTIFICATION_FEED_POLL_MS = 180_000;
/** Не чаще этого лента перезапрашивается по возврату фокуса в окно. */
export const NOTIFICATION_FEED_FOCUS_MIN_MS = 60_000;

/**
 * Одна страница ленты. `cursor === null` — первая.
 *
 * Запрос всегда «фоновый» для фетчера: его отказ показывает сама панель
 * (строкой внутри и отметкой на колокольчике), а не общий тост.
 */
export async function fetchNotificationFeedPage(
  cursor: string | null,
): Promise<NotificationFeedResponse> {
  const query = cursor === null ? '' : `?cursor=${encodeURIComponent(cursor)}`;
  return NotificationFeedResponseSchema.parse(
    await apiFetch<unknown>(`${FEED_PATH}${query}`, { background: true }),
  );
}

/**
 * Первая страница ленты актора — кормит счётчик колокольчика и верх панели.
 *
 * `enabled` управляется вызывающим: у супервайзера маршрут отвечает `403`, и
 * запрос не должен уходить вовсе — иначе каждая его вкладка писала бы отказ в
 * лог сервера на каждом такте.
 */
export function useNotificationFeedHead(userId: string | undefined, enabled: boolean) {
  return useQuery<NotificationFeedResponse, Error>({
    queryKey: qk.notificationFeed.mine(userId ?? '__pending__'),
    queryFn: () => fetchNotificationFeedPage(null),
    enabled: enabled && userId !== undefined,
    refetchInterval: NOTIFICATION_FEED_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Возврат сети офиса НЕ перезапрашивает ленту: иначе все вкладки всех людей
    // за одним адресом ударили бы разом — десятки запросов против всплеска 40,
    // и съели бы его у настоящих запросов. Лента обновится тактом или
    // открытием панели.
    refetchOnReconnect: false,
    staleTime: NOTIFICATION_FEED_FOCUS_MIN_MS,
    retry: false,
  });
}
